/**
 * 權限編輯器目錄：後端 permission（module.resource.action）→ 依「功能最小單位」成列，再依種類分區。
 * 種類是前端對照（後端沒有分類欄位）；未列入的 resource 落在「其他」。
 */

export const MODULE_LABELS = {
  ims: '倉儲',
  mom: '生產',
  crm: '客戶',
  aftersales: '售後',
  srm: '採購',
  fms: '財務',
  hr: '人資',
  sys: '系統',
};

/** 固定欄位；其餘 action 歸到「其他」 */
export const ACTION_COLUMNS = [
  { action: 'read', label: '讀取' },
  { action: 'create', label: '建立' },
  { action: 'update', label: '更新' },
  { action: 'review', label: '審核' },
];

export const EXTRA_ACTION_LABELS = {
  activate: '啟停',
  delete: '刪除',
  void: '作廢',
  cancel: '作廢',
  discard: '捨棄',
  close: '結案',
  complete: '完成',
  confirm: '確認',
  submit: '提交',
  process: '受理',
  pay: '撥款',
  receive: '領收',
  refund: '退款',
  correct: '更正',
  split: '拆分',
  self: '只看自己的',
};

export const PERMISSION_CATEGORIES = [
  {
    key: 'document',
    label: '單據類',
    resources: [
      'ims.stock_receipt',
      'ims.stock_issue',
      'ims.stock_transfer',
      'ims.stock_adjustment',
      'ims.stocktake',
      'ims.stock_request',
      'ims.stock_request_amendment',
      'ims.stock_return',
      'ims.stock_return_amendment',
      'ims.stock_reservation',
      'ims.stock_reservation_amendment',
      'ims.inspection_report',
      'mom.work_order',
      'mom.work_order_amendment',
      'mom.work_order_repair',
      'srm.purchase_order',
      'srm.misc_purchase_order',
      'crm.quotation',
      'crm.cost_estimate',
      'crm.customer_order',
      'crm.customer_order_amendment',
      'crm.sales_order',
      'aftersales.repair_request',
      'aftersales.repair_report',
      'aftersales.sign_receipt',
      'fms.payment_request',
      'fms.voucher',
      'hr.leave_request',
      'hr.overtime_request',
    ],
  },
  {
    key: 'master',
    label: '主檔類',
    resources: [
      'ims.mtr',
      'ims.inspection_kind',
      'mom.bom',
      'mom.processing_factory',
      'srm.supplier',
      'crm.customer',
      'crm.product',
      'aftersales.repair_item',
      'aftersales.repair_text_template',
      'aftersales.sn_product_mapping',
    ],
  },
  {
    key: 'operation',
    label: '庫存與收付',
    resources: [
      'ims.inventory_stock',
      'fms.customer_collection',
      'fms.purchase_payment',
    ],
  },
  {
    key: 'report',
    label: '報表查詢',
    resources: [
      'crm.customer_collection_summary',
      'fms.dashboard_financial',
    ],
  },
  {
    key: 'system',
    label: '系統設定',
    resources: [
      'sys.account',
      'sys.role',
      'sys.department',
      'sys.warehouse',
      'sys.stock_issue_category',
      'sys.currency',
      'sys.payment_term',
      'sys.message_rule',
      'hr.approval_policy',
    ],
  },
];

const OTHER_CATEGORY = { key: 'other', label: '其他' };

const NAME_VERB_PREFIXES = [
  '讀取自己建立的', '讀取', '建立', '維護', '更新', '審核', '管理', '啟停', '刪除', '作廢',
  '捨棄', '結案', '完成', '確認', '提交', '送審', '受理', '撥款', '領收', '登錄', '沖銷',
  '更正', '拆分', '設定', '由客訂建立',
];

const RESOURCE_INDEX = new Map(
  PERMISSION_CATEGORIES.flatMap((category) => (
    category.resources.map((resourceKey, order) => [resourceKey, { category, order }])
  )),
);

function stripVerb(name) {
  const text = String(name || '').trim();
  const prefix = NAME_VERB_PREFIXES.find((verb) => text.startsWith(verb));
  return (prefix ? text.slice(prefix.length) : text).trim();
}

function resolveRowLabel(perms) {
  const read = perms.find((perm) => perm.action === 'read');
  const source = read ?? perms.find((perm) => perm.action === 'create') ?? perms[0];
  // 後端名稱用「Dashboard」，畫面統一稱主控台
  return stripVerb(source?.name).replace(/Dashboard\s*/g, '主控台') || source?.resource || '—';
}

function splitCode(perm) {
  const [module, resource, action] = String(perm.code || '').split('.');
  return {
    module: perm.module || module,
    resource: perm.resource || resource,
    action: perm.action || action,
  };
}

const FIXED_ACTIONS = new Set(ACTION_COLUMNS.map((column) => column.action));

/**
 * @param {Array<{id: string, code: string, name?: string, module?: string, resource?: string, action?: string}>}
 *   catalog
 * @returns {Array<{key: string, label: string, rows: Array}>} 依種類排序，種類內依對照表順序
 */
export function buildPermissionSections(catalog) {
  const rowsByKey = new Map();

  (catalog ?? []).forEach((raw) => {
    const perm = { ...raw, ...splitCode(raw) };
    if (!perm.id || !perm.module || !perm.resource) {
      return;
    }
    const key = `${perm.module}.${perm.resource}`;
    if (!rowsByKey.has(key)) {
      rowsByKey.set(key, { key, module: perm.module, resource: perm.resource, perms: [] });
    }
    rowsByKey.get(key).perms.push(perm);
  });

  const sections = new Map(
    [...PERMISSION_CATEGORIES, OTHER_CATEGORY].map((category) => [
      category.key,
      { key: category.key, label: category.label, rows: [] },
    ]),
  );

  rowsByKey.forEach((row) => {
    const indexed = RESOURCE_INDEX.get(row.key);
    const actions = {};
    const extras = [];
    row.perms.forEach((perm) => {
      if (FIXED_ACTIONS.has(perm.action)) {
        actions[perm.action] = perm;
      } else {
        extras.push(perm);
      }
    });
    extras.sort((a, b) => a.action.localeCompare(b.action));

    sections.get(indexed?.category.key ?? OTHER_CATEGORY.key).rows.push({
      key: row.key,
      label: resolveRowLabel(row.perms),
      moduleLabel: MODULE_LABELS[row.module] ?? row.module,
      order: indexed?.order ?? Number.MAX_SAFE_INTEGER,
      actions,
      extras,
      perms: row.perms,
    });
  });

  return [...sections.values()]
    .map((section) => ({
      ...section,
      rows: section.rows.sort((left, right) => (
        left.order - right.order || left.key.localeCompare(right.key)
      )),
    }))
    .filter((section) => section.rows.length > 0);
}

/** 讀取以外、需要先能讀取的動作（「只看自己的」本身屬讀取類） */
export function needsRead(perm) {
  return perm.action !== 'read' && perm.action !== 'self';
}

export function getExtraActionLabel(perm) {
  return EXTRA_ACTION_LABELS[perm.action] ?? perm.action;
}

/** 搜尋：功能名稱、模組名稱、權限代碼或權限名稱任一包含關鍵字 */
export function rowMatchesKeyword(row, keyword) {
  const needle = keyword.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  return [row.label, row.moduleLabel, ...row.perms.flatMap((perm) => [perm.code, perm.name])]
    .some((text) => String(text || '').toLowerCase().includes(needle));
}
